/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {Schedule} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_SCHEDULE,
  ADD_ADMIN_SCHEDULE_ERROR,
  ADD_ADMIN_SCHEDULE_REQUEST,
  ADD_ADMIN_SCHEDULE_RESPONSE,
  ADMIN_SCHEDULES_GRID_REFRESHED,
  AUDIT_ADMIN_SCHEDULE,
  AUDIT_ADMIN_SCHEDULE_ERROR,
  AUDIT_ADMIN_SCHEDULE_REQUEST,
  AUDIT_ADMIN_SCHEDULE_RESPONSE,
  CLEAR_ADMIN_SCHEDULES_ERRORS,
  DELETE_ADMIN_SCHEDULE,
  DELETE_ADMIN_SCHEDULE_ERROR,
  DELETE_ADMIN_SCHEDULE_REQUEST,
  DELETE_ADMIN_SCHEDULE_RESPONSE,
  DELETE_ADMIN_SCHEDULES,
  DELETE_ADMIN_SCHEDULES_ERROR,
  DELETE_ADMIN_SCHEDULES_REQUEST,
  DELETE_ADMIN_SCHEDULES_RESPONSE,
  EDIT_ADMIN_SCHEDULE,
  EDIT_ADMIN_SCHEDULE_ERROR,
  EDIT_ADMIN_SCHEDULE_REQUEST,
  EDIT_ADMIN_SCHEDULE_RESPONSE,
  EDIT_ADMIN_SCHEDULES,
  EDIT_ADMIN_SCHEDULES_ERROR,
  EDIT_ADMIN_SCHEDULES_REQUEST,
  EDIT_ADMIN_SCHEDULES_RESPONSE,
  GET_ADMIN_SCHEDULES_ERROR,
  GET_ADMIN_SCHEDULES_REQUEST,
  GET_ADMIN_SCHEDULES_RESPONSE,
  INITIALIZE_ADMIN_SCHEDULES,
  MANAGE_ADMIN_SCHEDULE,
  MANAGE_ADMIN_SCHEDULE_ERROR,
  MANAGE_ADMIN_SCHEDULE_REQUEST,
  MANAGE_ADMIN_SCHEDULE_RESPONSE,
  RE_INITIALIZE_ADMIN_SCHEDULES,
  SET_ADMIN_SCHEDULES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-schedules';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

interface AdminSchedulesActionState extends AsyncState {
  selected: Schedule | Schedule[] | null;
  result: Schedule | Schedule[] | null;
  isEditing: boolean;
  record?: Schedule | Schedule[] | null;
};

export interface AdminSchedulesState extends PageState {
  schedules: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {},
  },
  add: AdminSchedulesActionState,
  edit: AdminSchedulesActionState,
  manage: AdminSchedulesActionState,
  audit: AdminSchedulesActionState,
  destroy: AdminSchedulesActionState,
  bulkEdit: AdminSchedulesActionState,
  bulkDelete: AdminSchedulesActionState
};

export interface AdminSchedulesAction extends AdminAction {
  schedule?: Schedule;
  schedules?: Schedule[];
};

// defines the user preference key for the admin schedules page ...
const PREFERENCE = 'admin-schedules';

// defines the initial state for the reducer ...
export const initialState: AdminSchedulesState = {
  isInitialized : false,
  schedules     : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  manage : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_SCHEDULES] : (draft: AdminSchedulesState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [CLEAR_ADMIN_SCHEDULES_ERRORS] : (draft: AdminSchedulesState) => {
    draft.destroy.error            = null;
    draft.bulkDelete.error         = null;
  },
  [RE_INITIALIZE_ADMIN_SCHEDULES] : (draft: AdminSchedulesState) => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, schedules}: AdminSchedulesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : schedules.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_SCHEDULES_GRID_FILTER_MODEL] : ({grid}: AdminSchedulesState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminSchedulesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_SCHEDULES_GRID_REFRESHED] : (
    {add, edit, manage, audit, destroy, bulkEdit, bulkDelete}: AdminSchedulesState
  ) => {
    add.result = null;
    edit.result = null;
    manage.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
  },
  [GET_ADMIN_SCHEDULES_REQUEST] : ({schedules}: AdminSchedulesState) => {
    schedules.loading = true;
  },
  [GET_ADMIN_SCHEDULES_RESPONSE] : ({schedules}: AdminSchedulesState) => {
    schedules.loading = false;
  },
  [GET_ADMIN_SCHEDULES_ERROR] : (
    {schedules}: AdminSchedulesState, {error}: AdminSchedulesAction
  ) => {
    schedules.loading = false;
    schedules.error   = error;
  },
  [ADD_ADMIN_SCHEDULE] : ({add}: AdminSchedulesState, {show}: AdminSchedulesAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_SCHEDULE_REQUEST] : ({add}: AdminSchedulesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_SCHEDULE_RESPONSE] : ({add}: AdminSchedulesState, {schedule}: AdminSchedulesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = schedule;
  },
  [ADD_ADMIN_SCHEDULE_ERROR] : ({add}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_SCHEDULE] : ({edit}: AdminSchedulesState, {schedule, show}: AdminSchedulesAction) => {
    edit.selected  = show ? schedule : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_SCHEDULE_REQUEST] : ({edit}: AdminSchedulesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_SCHEDULE_RESPONSE] : ({edit}: AdminSchedulesState, {schedule}: AdminSchedulesAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = schedule;
  },
  [EDIT_ADMIN_SCHEDULE_ERROR] : ({edit}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [MANAGE_ADMIN_SCHEDULE] : ({manage}: AdminSchedulesState, {schedule, show}: AdminSchedulesAction) => {
    manage.selected  = show ? schedule : null;
    manage.isEditing = show;
    manage.result    = null;
  },
  [MANAGE_ADMIN_SCHEDULE_REQUEST] : ({manage}: AdminSchedulesState) => {
    manage.loading = true;
    manage.error   = manage.result = null;
  },
  [MANAGE_ADMIN_SCHEDULE_RESPONSE] : ({manage}: AdminSchedulesState, {schedule}: AdminSchedulesAction) => {
    manage.loading   = false;
    manage.isEditing = false;
    manage.result    = schedule;
  },
  [MANAGE_ADMIN_SCHEDULE_ERROR] : ({manage}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    manage.loading   = false;
    manage.error     = error;
  },
  [AUDIT_ADMIN_SCHEDULE] : ({audit}: AdminSchedulesState, {schedule, show}: AdminSchedulesAction) => {
    audit.selected  = show ? schedule : null;
    audit.isEditing = show;
    audit.result    = null;
  },
  [AUDIT_ADMIN_SCHEDULE_REQUEST] : ({audit}: AdminSchedulesState) => {
    audit.loading = true;
    audit.error   = audit.result = null;
  },
  [AUDIT_ADMIN_SCHEDULE_RESPONSE] : ({audit}: AdminSchedulesState, {result}: AdminSchedulesAction) => {
    audit.loading   = false;
    audit.isEditing = false;
    audit.result    = result;
  },
  [AUDIT_ADMIN_SCHEDULE_ERROR] : ({audit}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    audit.loading   = false;
    audit.error     = error;
  },
  [DELETE_ADMIN_SCHEDULE] : ({destroy}: AdminSchedulesState, {schedule, show}: AdminSchedulesAction) => {
    destroy.selected  = show ? schedule : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_SCHEDULE_REQUEST] : ({destroy}: AdminSchedulesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_SCHEDULE_RESPONSE] : ({destroy}: AdminSchedulesState, {schedule}: AdminSchedulesAction) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = schedule;
  },
  [DELETE_ADMIN_SCHEDULE_ERROR] : ({destroy}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [EDIT_ADMIN_SCHEDULES] : ({bulkEdit}: AdminSchedulesState, {schedules, show}: AdminSchedulesAction) => {
    bulkEdit.selected  = show ? schedules : null;
    bulkEdit.isEditing = show;
    bulkEdit.result    = null;
  },
  [EDIT_ADMIN_SCHEDULES_REQUEST] : ({bulkEdit}: AdminSchedulesState) => {
    bulkEdit.loading = true;
    bulkEdit.error   = bulkEdit.result = null;
  },
  [EDIT_ADMIN_SCHEDULES_RESPONSE] : ({bulkEdit}: AdminSchedulesState, {schedules}: AdminSchedulesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.isEditing = false;
    bulkEdit.result    = schedules;
  },
  [EDIT_ADMIN_SCHEDULES_ERROR] : ({bulkEdit}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.error     = error;
  },
  [DELETE_ADMIN_SCHEDULES] : ({bulkDelete}: AdminSchedulesState, {schedules, show}: AdminSchedulesAction) => {
    bulkDelete.selected  = show ? schedules : null;
    bulkDelete.isEditing = show;
    bulkDelete.result    = null;
    bulkDelete.record    = null;
  },
  [DELETE_ADMIN_SCHEDULES_REQUEST] : ({bulkDelete}: AdminSchedulesState) => {
    bulkDelete.loading = true;
    bulkDelete.error   = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_SCHEDULES_RESPONSE] : (
    {bulkDelete}: AdminSchedulesState, {schedules}: AdminSchedulesAction
  ) => {
    bulkDelete.loading   = false;
    bulkDelete.isEditing = false;
    bulkDelete.result    = bulkDelete.record = schedules;
  },
  [DELETE_ADMIN_SCHEDULES_ERROR] : ({bulkDelete}: AdminSchedulesState, {error}: AdminSchedulesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.error     = error;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminSchedulesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
